<template>
  <div class="product-picker">
    <div class="position-relative">
      <div class="input-group">
        <span class="input-group-text"><i class="bi bi-search"></i></span>
        <input v-model="search" type="search" class="form-control" placeholder="Search products by name, code or SKU"
               @input="onSearch" @focus="showResults = results.length > 0">
      </div>
      <div v-if="showResults" class="picker-results shadow border rounded bg-white">
        <div v-if="searching" class="p-2 small text-muted">Searching...</div>
        <button v-for="product in results" :key="product.id" type="button"
                class="picker-result d-flex align-items-center gap-2 w-100 text-start border-0 bg-transparent"
                :disabled="isSelected(product.id) || modelValue.length >= max"
                @click="add(product)">
          <img :src="product.avatar || placeholder" alt="" class="picker-thumb">
          <span class="flex-grow-1 text-truncate">{{ product.name }}</span>
          <i v-if="isSelected(product.id)" class="bi bi-check-circle-fill text-success"></i>
          <i v-else class="bi bi-plus-circle text-primary"></i>
        </button>
        <div v-if="!searching && !results.length" class="p-2 small text-muted">No products found</div>
      </div>
    </div>

    <p class="small text-muted mt-2 mb-1">
      {{ modelValue.length }} / {{ max }} selected. Products are shown in this order, and only if they're in stock at the store.
    </p>
    <ul class="list-group">
      <li v-for="(product, index) in selected" :key="product.id" class="list-group-item d-flex align-items-center gap-2 py-1">
        <img :src="product.avatar || placeholder" alt="" class="picker-thumb">
        <span class="flex-grow-1 text-truncate small">{{ product.name }}</span>
        <button type="button" class="btn btn-sm btn-link text-secondary p-0" :disabled="index === 0" @click="move(index, -1)" title="Move up">
          <i class="bi bi-arrow-up"></i>
        </button>
        <button type="button" class="btn btn-sm btn-link text-secondary p-0" :disabled="index === selected.length - 1" @click="move(index, 1)" title="Move down">
          <i class="bi bi-arrow-down"></i>
        </button>
        <button type="button" class="btn btn-sm btn-link text-danger p-0" @click="remove(product.id)" title="Remove">
          <i class="bi bi-x-circle"></i>
        </button>
      </li>
    </ul>
  </div>
</template>

<script>
import productService from "@/api/productService";

export default {
  name: "ProductPicker",

  emits: ['update:modelValue'],

  props: {
    // Array of product ids
    modelValue: { type: Array, default: () => [] },
    // Already known product objects ({id, name, avatar}) for the ids
    initialProducts: { type: Array, default: () => [] },
    max: { type: Number, default: 48 },
  },

  data() {
    return {
      search: '',
      results: [],
      searching: false,
      showResults: false,
      known: {},
      timer: null,
      placeholder: process.env.VUE_APP_API_PUBLIC_URL + 'images/placeholder.svg',
    }
  },

  computed: {
    selected() {
      return this.modelValue.map(id => this.known[id] || { id, name: 'Product', avatar: null })
    },
  },

  watch: {
    initialProducts: {
      immediate: true,
      handler(products) {
        (products || []).forEach(p => { this.known[p.id] = p })
      },
    },
  },

  methods: {
    onSearch() {
      clearTimeout(this.timer)
      if (this.search.trim().length < 2) {
        this.results = []
        this.showResults = false
        return
      }
      this.timer = setTimeout(this.runSearch, 350)
    },
    async runSearch() {
      this.searching = true
      this.showResults = true
      try {
        const response = await productService.getProducts({ search: this.search.trim(), per_page: 15 })
        this.results = response?.data || []
      } finally {
        this.searching = false
      }
    },
    isSelected(id) {
      return this.modelValue.includes(id)
    },
    add(product) {
      if (this.isSelected(product.id) || this.modelValue.length >= this.max) return
      this.known[product.id] = { id: product.id, name: product.name, avatar: product.avatar }
      this.$emit('update:modelValue', [...this.modelValue, product.id])
    },
    remove(id) {
      this.$emit('update:modelValue', this.modelValue.filter(v => v !== id))
    },
    move(index, delta) {
      const list = [...this.modelValue]
      const [value] = list.splice(index, 1)
      list.splice(index + delta, 0, value)
      this.$emit('update:modelValue', list)
    },
  },

  mounted() {
    this.outsideClick = (e) => {
      if (!this.$el.contains(e.target)) this.showResults = false
    }
    document.addEventListener('click', this.outsideClick)
  },

  beforeUnmount() {
    document.removeEventListener('click', this.outsideClick)
    clearTimeout(this.timer)
  },
}
</script>

<style scoped>
.picker-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 20;
  max-height: 280px;
  overflow-y: auto;
}

.picker-result {
  padding: 6px 10px;
}

.picker-result:hover:not(:disabled) {
  background: #f1f5f9 !important;
}

.picker-result:disabled {
  opacity: .6;
}

.picker-thumb {
  width: 32px;
  height: 32px;
  object-fit: cover;
  border-radius: 4px;
  background: #f1f3f5;
  flex-shrink: 0;
}
</style>
